import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle, ArrowRight, Briefcase, CheckCircle2, Clock, FilePlus2, FolderOpen,
} from "lucide-react";
import {
  Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";
import { STATUS_CHART_COLORS, fmtDate, useLabels } from "../lib/constants";
import { PriorityBadge, StatusBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

const COMPANY_COLORS = { b2b: "#1E2B68", outpatient: "#8CA63B" };

export default function Dashboard() {
  const { user } = useAuth();
  const { t, rtl } = useLanguage();
  const L = useLabels();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    setMounted(true);
  }, []);

  if (!data) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="dashboard-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-delta-navy border-t-transparent" />
      </div>
    );
  }

  const statusChart = Object.entries(data.by_status).map(([k, v]) => ({
    name: L.status[k] || k, value: v, color: STATUS_CHART_COLORS[k] || "#94a3b8",
  }));
  const companyChart = Object.entries(data.by_company).map(([k, v]) => ({
    name: L.company[k] || k, value: v, color: COMPANY_COLORS[k] || "#94a3b8",
  }));

  const stats = [
    { label: t("dashboard.total"), value: data.total, icon: Briefcase, testid: "stat-total-cases", accent: "text-delta-navy bg-indigo-50" },
    { label: t("dashboard.open"), value: data.open, icon: FolderOpen, testid: "stat-open-cases", accent: "text-blue-700 bg-blue-50" },
    { label: t("dashboard.resolved"), value: data.resolved, icon: CheckCircle2, testid: "stat-resolved-cases", accent: "text-green-700 bg-green-50" },
    { label: t("dashboard.avg"), value: data.avg_resolution_days != null ? `${data.avg_resolution_days}d` : "—", icon: Clock, testid: "stat-avg-resolution", accent: "text-[#5c7023] bg-delta-limeLight" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {t("dashboard.welcome", { name: user.name.split(" ")[0] })}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {t("dashboard.overview", { role: L.role[user.role] })}
          </p>
        </div>
        <Button
          data-testid="dashboard-new-case-button"
          onClick={() => navigate("/cases/new")}
          className="bg-delta-navy hover:bg-delta-navyDark"
        >
          <FilePlus2 className="me-2 h-4 w-4" /> {t("dashboard.submit")}
        </Button>
      </div>

      {data.urgent_open > 0 && (
        <div data-testid="urgent-banner" className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" />
          <p className="text-sm font-medium text-red-800">
            {t(data.urgent_open === 1 ? "dashboard.urgentOne" : "dashboard.urgentMany", { count: data.urgent_open })}
          </p>
          <Link to="/cases?priority=urgent" data-testid="urgent-banner-link" className="ms-auto flex items-center gap-1 text-sm font-semibold text-red-700 hover:underline">
            {t("dashboard.review")} <ArrowRight className={`h-3.5 w-3.5 ${rtl ? "rotate-180" : ""}`} />
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((s, i) => (
          <Card key={s.label} data-testid={s.testid} className="animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
            <CardContent className="flex items-center gap-4 p-5">
              <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${s.accent}`}>
                <s.icon className="h-5 w-5" />
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-slate-900">{s.value}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card data-testid="chart-status">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">{t("dashboard.byStatus")}</CardTitle>
          </CardHeader>
          <CardContent className="h-64 min-w-[200px]">
            {!mounted || statusChart.length === 0 ? (
              <p className="flex h-full items-center justify-center text-sm text-slate-400">{t("dashboard.noCases")}</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={200}>
                <BarChart data={statusChart} margin={{ top: 8, right: 8, left: -18, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" interval={0} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {statusChart.map((e) => <Cell key={e.name} fill={e.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card data-testid="chart-company">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">{t("dashboard.b2bOut")}</CardTitle>
          </CardHeader>
          <CardContent className="h-64 min-w-[200px]">
            {!mounted || companyChart.length === 0 ? (
              <p className="flex h-full items-center justify-center text-sm text-slate-400">{t("dashboard.noCases")}</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={200}>
                <PieChart>
                  <Pie data={companyChart} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                    {companyChart.map((e) => <Cell key={e.name} fill={e.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
            <div className="-mt-6 flex justify-center gap-5">
              {companyChart.map((e) => (
                <div key={e.name} className="flex items-center gap-1.5 text-xs text-slate-600">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: e.color }} />
                  {e.name} ({e.value})
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="recent-cases-card">
        <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="font-heading text-lg">{t("dashboard.recent")}</CardTitle>
          <Link to="/cases" data-testid="view-all-cases-link" className="flex items-center gap-1 text-sm font-semibold text-delta-navy hover:underline">
            {t("common.viewAll")} <ArrowRight className={`h-3.5 w-3.5 ${rtl ? "rotate-180" : ""}`} />
          </Link>
        </CardHeader>
        <CardContent className="p-0">
          {data.recent.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <p className="text-sm text-slate-500">{t("dashboard.emptyText")}</p>
              <Button data-testid="empty-new-case-button" onClick={() => navigate("/cases/new")} className="mt-4 bg-delta-navy hover:bg-delta-navyDark">
                <FilePlus2 className="me-2 h-4 w-4" /> {t("dashboard.emptyCta")}
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {data.recent.map((c) => (
                <button
                  key={c.id}
                  data-testid={`recent-case-${c.case_number}`}
                  onClick={() => navigate(`/cases/${c.id}`)}
                  className="flex w-full flex-wrap items-center gap-3 px-6 py-3.5 text-start transition-colors hover:bg-delta-limeLight/40"
                >
                  <span className="font-mono text-xs font-semibold text-delta-navy">{c.case_number}</span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{c.title}</span>
                  <PriorityBadge priority={c.priority} />
                  <StatusBadge status={c.status} />
                  <span className="text-xs text-slate-400">{fmtDate(c.created_at)}</span>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
